import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Song } from '../models/song.model';

/** Raw item from GET /songs/charts — fields may vary; mapping normalizes to {@link Song}. */
export interface ApiChartSong {
  id: number | string;
  title?: string;
  company?: string;
  award?: string;
  awards?: Array<string | { type?: string; code?: string }>;
  rank?: number;
  prevWeekRank?: number | null;
  prev_week_rank?: number | null;
  last_week_rank?: number | null;
  lastWeekRank?: number | null;
  weeksOnChart?: number | null;
  weeklyStreak?: number | null;
  bestRank?: number | null;
  bestPosition?: { rank?: number | null } | null;
  bestRankWeek?: number | string | null;
  bestRankYear?: number | string | null;
  bestWeek?: string | null;
  bestYear?: string | null;
  status?: string;
  artist?: string | { name?: string; id?: number; slug?: string };
  artists?: Array<{ name?: string; id?: number; slug?: string | null }>;
}

export interface SongsChartsResponse {
  status: string;
  data: {
    songs: ApiChartSong[];
    lastScraped?: string;
    lastScrapedAt?: string;
    year?: number;
    week?: number;
    latestYear?: number;
    latestWeek?: number;
  };
}

export interface SongsChartsResult {
  songs: Song[];
  lastScraped: Date | null;
  /** Chart ISO week-year and week when returned by the API (or from request params). */
  year?: number;
  week?: number;
  latestYear?: number;
  latestWeek?: number;
}

/** One weekly chart payload plus its week identity (for multi-week / forkJoin loads). */
export interface SongsChartsWeekSnapshot extends SongsChartsResult {
  year: number;
  week: number;
}

@Injectable({ providedIn: 'root' })
export class SongsService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/songs`;

  /**
   * GET /songs/latest-week
   */
  getLatestWeek(): Observable<{ year: number; week: number } | null> {
    return this.http
      .get<{ status: string; data: { year: number; week: number } | null }>(
        `${this.baseUrl}/latest-week`
      )
      .pipe(map((res) => res.data));
  }

  /**
   * GET /songs/available-weeks
   */
  getAvailableWeeks(): Observable<Array<{ year: number; week: number }>> {
    return this.http
      .get<{ status: string; data: Array<{ year: number; week: number }> }>(
        `${this.baseUrl}/available-weeks`
      )
      .pipe(map((res) => res.data ?? []));
  }


  /**
   * GET /songs/charts — optional year & week as query params.
   */
  getCharts(year?: number, week?: number): Observable<SongsChartsResult> {
    let params = new HttpParams();
    if (year != null && !Number.isNaN(year)) params = params.set('year', String(year));
    if (week != null && !Number.isNaN(week)) params = params.set('week', String(week));

    return this.http.get<SongsChartsResponse>(`${this.baseUrl}/charts`, { params }).pipe(
      map((response) => {
        const raw = response.data?.songs ?? [];
        const last =
          response.data?.lastScraped ?? response.data?.lastScrapedAt ?? null;
        const y = response.data?.year;
        const w = response.data?.week;
        const ly = response.data?.latestYear;
        const lw = response.data?.latestWeek;
        return {
          songs: raw.map((item) => this.mapApiSongToSong(item)),
          lastScraped: last ? new Date(last) : null,
          year: y != null && !Number.isNaN(Number(y)) ? Number(y) : year,
          week: w != null && !Number.isNaN(Number(w)) ? Number(w) : week,
          latestYear: ly != null && !Number.isNaN(Number(ly)) ? Number(ly) : undefined,
          latestWeek: lw != null && !Number.isNaN(Number(lw)) ? Number(lw) : undefined,
        };
      })
    );
  }

  /**
   * Loads `weekCount` consecutive published chart weeks (newest first).
   * Anchors on the same week as GET /charts (no params), then walks back using API chart week numbers
   * (IFPI week — not ISO calendar week).
   */
  getChartsForRange(weekCount: number): Observable<SongsChartsWeekSnapshot[]> {
    const n = Math.max(1, Math.floor(weekCount));
    return this.getCharts().pipe(
      switchMap((latest) => from(this.loadWeekSnapshotsFromLatest(latest, n)))
    );
  }

  private async loadWeekSnapshotsFromLatest(
    latest: SongsChartsResult,
    weekCount: number
  ): Promise<SongsChartsWeekSnapshot[]> {
    const anchor = await this.resolvePublishedChartWeek(latest);
    const keys = anchor
      ? this.buildChartWeekKeysFromAnchor(anchor, weekCount)
      : this.buildDistinctIsoWeekKeysFromDate(new Date(), weekCount);

    const snapshots: SongsChartsWeekSnapshot[] = [];
    for (let i = 0; i < keys.length; i++) {
      const { year, week } = keys[i];
      if (i === 0 && latest.songs.length > 0) {
        snapshots.push({ ...latest, year, week });
        continue;
      }
      const res = await firstValueFrom(this.getCharts(year, week));
      snapshots.push({
        ...res,
        year: res.year ?? year,
        week: res.week ?? week,
      });
    }
    return snapshots;
  }

  /**
   * Finds which `year`/`week` query params return the same chart as GET /charts (no params).
   * The API uses chart publication weeks (e.g. 2026-W16), not ISO weeks (e.g. 2026-W21).
   */
  private async resolvePublishedChartWeek(
    latest: SongsChartsResult
  ): Promise<{ year: number; week: number } | null> {
    if (latest.year != null && latest.week != null) {
      return { year: latest.year, week: latest.week };
    }

    const probeId = latest.songs[0]?.id;
    if (!probeId) return null;

    const seen = new Set<string>();
    for (let i = 0; i < 24; i++) {
      const d = new Date();
      d.setDate(d.getDate() - 7 * i);
      const { weekYear, week } = this.getIsoWeekYearAndWeek(d);
      const key = `${weekYear}-W${week}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const res = await firstValueFrom(this.getCharts(weekYear, week));
      if (
        res.songs.length > 0 &&
        String(res.songs[0]?.id ?? '') === String(probeId)
      ) {
        return { year: weekYear, week };
      }
    }
    return null;
  }

  /** Chart weeks counting down from the published anchor (W16 → W15 → …). */
  private buildChartWeekKeysFromAnchor(
    anchor: { year: number; week: number },
    count: number
  ): Array<{ year: number; week: number }> {
    const out: Array<{ year: number; week: number }> = [];
    let { year, week } = anchor;
    while (out.length < count) {
      out.push({ year, week });
      ({ year, week } = this.decrementChartWeek(year, week));
    }
    return out;
  }

  private decrementChartWeek(year: number, week: number): { year: number; week: number } {
    if (week > 1) return { year, week: week - 1 };
    return { year: year - 1, week: 52 };
  }

  /** ISO week-year and week number for a calendar date (ISO 8601). */
  private getIsoWeekYearAndWeek(d: Date): { weekYear: number; week: number } {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day + 3);
    const weekYear = x.getFullYear();
    const week1 = new Date(weekYear, 0, 4);
    const w1d = (week1.getDay() + 6) % 7;
    week1.setDate(week1.getDate() - w1d);
    const mon = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const monD = (mon.getDay() + 6) % 7;
    mon.setDate(mon.getDate() - monD);
    const week = 1 + Math.floor((mon.getTime() - week1.getTime()) / (7 * 86400000));
    return { weekYear, week };
  }

  /** Newest ISO week first; skips accidental duplicates when stepping by 7 days. */
  private buildDistinctIsoWeekKeysFromDate(
    from: Date,
    count: number
  ): Array<{ year: number; week: number }> {
    const out: Array<{ year: number; week: number }> = [];
    const seen = new Set<string>();
    for (let i = 0; out.length < count; i++) {
      const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
      d.setDate(d.getDate() - 7 * i);
      const { weekYear, week } = this.getIsoWeekYearAndWeek(d);
      const key = `${weekYear}-W${week}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ year: weekYear, week });
    }
    return out;
  }

  /** Exported for tests / reuse. */
  mapApiSongToSong(raw: ApiChartSong): Song {
    const rank = Number(raw.rank ?? 0);
    const prevWeekNum = this.coerceOptionalNumber(
      raw.prevWeekRank ?? raw.prev_week_rank ?? raw.lastWeekRank ?? raw.last_week_rank
    );

    const { displayArtist, artistIds } = this.normalizeArtists(raw);

    const numWeeks = Number(raw.weeklyStreak ?? 0) || 0;

    const bestFromApi =
      raw.bestRank != null ? Number(raw.bestRank) : raw.bestPosition?.rank != null ? Number(raw.bestPosition.rank) : undefined;
    const bestPosition =
      bestFromApi != null && !Number.isNaN(bestFromApi) ? bestFromApi : undefined;

    const bestWeek = raw.bestWeek != null ? String(raw.bestWeek) : raw.bestRankWeek != null ? String(raw.bestRankWeek) : undefined;
    const bestYear = raw.bestYear != null ? String(raw.bestYear) : raw.bestRankYear != null ? String(raw.bestRankYear) : undefined;

    const award = this.resolveAwardBadge(raw.awards, raw.award);
    const weekMinus1ForUi = this.effectivePreviousRankForUi(prevWeekNum);

    return {
      id: raw.id != null && raw.id !== '' ? String(raw.id) : '',
      rank,
      title: String(raw.title ?? ''),
      artist: displayArtist,
      artistIds: artistIds.length ? artistIds : undefined,
      company: raw.company,
      award: award ?? undefined,
      prevWeekRank: prevWeekNum,
      weekMinus1: weekMinus1ForUi,
      numWeeks,
      bestPosition,
      bestWeek,
      bestYear,
      chartApiStatus: raw.status != null ? String(raw.status) : undefined,
    };
  }

  private coerceOptionalNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }

  private effectivePreviousRankForUi(lastWeekNum: number | null): number | undefined {
    if (lastWeekNum == null) return undefined;
    if (lastWeekNum === 0 || lastWeekNum >= 100) return undefined;
    return lastWeekNum;
  }

  private normalizeArtists(raw: ApiChartSong): { displayArtist: string; artistIds: string[] } {
    if (Array.isArray(raw.artists) && raw.artists.length > 0) {
      const validArtists = raw.artists.filter((a) => a && String(a?.name ?? '').trim() !== '');

      const names = validArtists.map((a) => String(a?.name ?? '').trim());

      const ids = validArtists.map((a) => {
        const slug = a?.slug != null && String(a.slug).trim() ? String(a.slug).trim() : '';
        const id = a?.id != null ? String(a.id) : '';
        return slug || id || '';
      });

      return { displayArtist: names.join(', '), artistIds: ids };
    }

    const a = raw.artist;
    if (typeof a === 'string') {
      return { displayArtist: a.trim(), artistIds: [] };
    }
    if (a && typeof a === 'object') {
      const name = String(a.name ?? '').trim();
      const idPart = a.slug != null && String(a.slug).trim() ? String(a.slug).trim() : a.id != null ? String(a.id) : '';
      return { displayArtist: name, artistIds: idPart ? [idPart] : [] };
    }

    return { displayArtist: '', artistIds: [] };
  }

  /**
   * Single badge: Diamond D > Platinum (P, 2P, 3P, 4P, …) > Gold G.
   */
  private resolveAwardBadge(
    awards: ApiChartSong['awards'],
    fallbackAward: string | undefined
  ): string | null {
    const codes: string[] = [];

    if (Array.isArray(awards)) {
      for (const entry of awards) {
        if (typeof entry === 'string' && entry.trim()) codes.push(entry.trim().toUpperCase());
        else if (entry && typeof entry === 'object') {
          const c = (entry.type ?? entry.code ?? '').toString().trim().toUpperCase();
          if (c) codes.push(c);
        }
      }
    }

    if (fallbackAward != null && String(fallbackAward).trim()) {
      codes.push(String(fallbackAward).trim().toUpperCase());
    }

    if (codes.length === 0) return null;

    if (codes.some((c) => c === 'D')) return 'D';

    const platinumCodes = codes.filter((c) => c === 'P' || /^\d+P$/.test(c));
    if (platinumCodes.length) {
      const tier = (c: string): number => {
        if (c === 'P') return 1;
        const m = /^(\d+)P$/.exec(c);
        return m ? Number(m[1]) : 0;
      };
      platinumCodes.sort((a, b) => tier(b) - tier(a));
      return platinumCodes[0];
    }

    if (codes.some((c) => c === 'G')) return 'G';

    return codes[0];
  }

  getSongDetailed(id: string): Observable<{ status: string; data: { song: any } }> {
    return this.http.get<{ status: string; data: { song: any } }>(`${this.baseUrl}/${id}`);
  }

  getAlbumDetailed(id: string): Observable<{ status: string; data: { album: any } }> {
    return this.http.get<{ status: string; data: { album: any } }>(`${this.baseUrl}/albums/${id}`);
  }
}
